import React, { useEffect, useRef, useState } from "react";
import { Loader2, Check, PenLine, Sparkles, Upload, ImageIcon, X } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";

const inputCls = "w-full bg-muted/50 border border-border rounded-xl px-4 py-3 focus:ring-2 focus:ring-primary/50 focus:border-primary outline-none transition-colors";

const SUGGESTIONS = [
  { label: "Pour un parent", text: "Pour toi Maman, avec tout notre amour. Tu es notre repère, notre douceur, chaque jour." },
  { label: "Souvenir d'enfance", text: "En souvenir de nos étés à la mer, du sable chaud et de ton rire — tes petits-enfants qui t'aiment." },
  { label: "Mots d'apaisement", text: "Tu es en sécurité, tu es aimé(e). Respire doucement, nous sommes là, tout près de toi." },
  { label: "Date qui compte", text: "Le 14 juin 1962 — le jour où tout a commencé. Avec amour, pour toujours." },
  { label: "Pour un enfant", text: "Pour mon petit trésor. Ce cocon est à toi, pour te sentir bien, à ton rythme, en douceur." },
];

const THEMES = [
  { key: "Souvenirs de Famille", sentence: "Un mariage, des vacances, un repas de fête… Choisissez une photo qui fait renaître la chaleur des vôtres." },
  { key: "Vie Professionnelle", sentence: "Un métier aimé, une fierté de toute une vie : une image qui rappelle avec tendresse qui il ou elle a été." },
  { key: "Animaux Aimés", sentence: "Un compagnon fidèle, une présence douce — les animaux réveillent souvent les émotions les plus heureuses." },
  { key: "La Maison", sentence: "Le jardin, la cuisine, le fauteuil préféré… Les lieux du quotidien qui rassurent et ancrent les souvenirs." },
];

const TECHNIQUES = [
  { key: "Gravure", hint: "Prénom ou mot gravé dans le bois ou le métal" },
  { key: "Couture", hint: "Tissus doux assemblés à la main" },
  { key: "Broderie", hint: "Fil brodé avec délicatesse" },
  { key: "Impression 3D", hint: "Objet tactile aux formes douces" },
  { key: "Flocage", hint: "Message imprimé sur le textile" },
];

const Personnalisation = () => {
  const [form, setForm] = useState({ name: "", email: "", product: "", message: "", resident_first_name: "", theme: "", technique: "" });
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState("");
  const fileRef = useRef(null);

  useEffect(() => { api.get("/products").then((r) => setProducts(r.data)).catch(() => {}); }, []);
  useEffect(() => () => { if (previewUrl) URL.revokeObjectURL(previewUrl); }, [previewUrl]);

  const applySuggestion = (text) => {
    setForm((f) => ({ ...f, message: f.message ? `${f.message.trim()} ${text}` : text }));
    toast.success("Suggestion ajoutée à votre message");
  };

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 10 * 1024 * 1024) { toast.error("Image trop volumineuse (max 10 Mo)"); return; }
    setFile(f);
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(URL.createObjectURL(f));
  };
  const clearFile = () => { setFile(null); if (previewUrl) URL.revokeObjectURL(previewUrl); setPreviewUrl(""); if (fileRef.current) fileRef.current.value = ""; };

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      let photo_url = "";
      if (file) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await api.post("/personalisation/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        photo_url = data.url;
      }
      await api.post("/personalisation", { ...form, photo_url });
      setDone(true);
      toast.success("Votre personnalisation a bien été transmise !");
      setForm({ name: "", email: "", product: "", message: "", resident_first_name: "", theme: "", technique: "" });
      clearFile();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
    setLoading(false);
  };

  return (
    <div className="pt-32 pb-10">
      <section className="max-w-6xl mx-auto px-6 lg:px-10">
        <Reveal>
          <p className="text-sm uppercase tracking-[0.3em] text-primary mb-5">Personnalisation standard</p>
          <h1 className="font-serif-display text-5xl sm:text-6xl font-light leading-tight">Composez une personnalisation pleine de sens</h1>
          <p className="mt-6 text-lg text-foreground/70 max-w-2xl leading-relaxed">
            Un prénom, une photo chère, un thème porteur de souvenirs heureux et un mot doux : rassemblez les éléments
            qui rendront l'objet unique pour votre proche.
          </p>
        </Reveal>

        <Reveal delay={0.15}>
          <div className="mt-12 rounded-[2.5rem] overflow-hidden border border-warm/25 shadow-[0_20px_60px_rgb(0,0,0,0.08)] grain" data-testid="perso-banner">
            <img src="/affiche-personnalisation.jpg" alt="Des mains d'artisane peignent avec soin un cœur en résine fleuri, entourées de photos de famille et de rubans — une gardienne veille" className="w-full h-auto block" />
          </div>
        </Reveal>

        {done ? (
          <Reveal delay={0.1}>
            <div className="mt-12 bg-secondary/20 border border-secondary/40 rounded-3xl p-10 text-center max-w-2xl" data-testid="perso-success">
              <div className="w-14 h-14 rounded-full bg-secondary/40 flex items-center justify-center mx-auto"><Check className="w-7 h-7 text-secondary-foreground" /></div>
              <h3 className="font-serif-display text-2xl mt-5">Personnalisation reçue avec soin</h3>
              <p className="text-foreground/70 mt-3">Nous reviendrons vers vous pour confirmer votre projet.</p>
              <button onClick={() => setDone(false)} className="mt-6 text-primary underline">Créer une autre personnalisation</button>
            </div>
          </Reveal>
        ) : (
          <div className="mt-12 grid lg:grid-cols-5 gap-10">
            {/* FORM */}
            <form onSubmit={submit} className="lg:col-span-3 space-y-6" data-testid="perso-form">
              <div className="grid sm:grid-cols-2 gap-5">
                <label className="block">
                  <span className="text-sm text-foreground/70 mb-2 block">Votre nom</span>
                  <input required className={inputCls} data-testid="perso-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
                </label>
                <label className="block">
                  <span className="text-sm text-foreground/70 mb-2 block">Email</span>
                  <input required type="email" className={inputCls} data-testid="perso-email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
                </label>
              </div>

              <label className="block">
                <span className="text-sm text-foreground/70 mb-2 block">Prénom du résident</span>
                <input className={inputCls} data-testid="perso-resident" value={form.resident_first_name} onChange={(e) => setForm({ ...form, resident_first_name: e.target.value })} placeholder="Ex : Jeanne" />
              </label>

              {/* THEMES */}
              <div data-testid="theme-section">
                <span className="text-sm text-foreground/70 mb-3 block">Choisissez un thème porteur de souvenirs heureux</span>
                <div className="grid sm:grid-cols-2 gap-3">
                  {THEMES.map((t) => (
                    <button
                      key={t.key}
                      type="button"
                      data-testid={`theme-${t.key.toLowerCase().replace(/[^a-z]/g, "")}`}
                      onClick={() => setForm({ ...form, theme: t.key })}
                      className={`text-left rounded-2xl border p-4 transition-colors ${form.theme === t.key ? "border-primary bg-primary/5 ring-2 ring-primary/30" : "border-border bg-card hover:border-primary/50"}`}
                    >
                      <span className="font-medium block">{t.key}</span>
                      <span className="text-xs text-foreground/60 leading-relaxed mt-1 block">{t.sentence}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* TECHNIQUE */}
              <div data-testid="technique-section">
                <span className="text-sm text-foreground/70 mb-3 block">Choisissez la technique de fabrication</span>
                <div className="flex flex-wrap gap-2">
                  {TECHNIQUES.map((t) => (
                    <button
                      key={t.key}
                      type="button"
                      data-testid={`technique-choice-${t.key.toLowerCase().replace(/[^a-z0-9]/g, "")}`}
                      onClick={() => setForm({ ...form, technique: t.key })}
                      title={t.hint}
                      className={`rounded-full border px-4 py-2 text-sm transition-colors ${form.technique === t.key ? "border-primary bg-primary text-primary-foreground" : "border-border bg-card hover:border-primary/50"}`}
                    >
                      {t.key}
                    </button>
                  ))}
                </div>
                {form.technique && <p className="text-xs text-foreground/60 mt-2">{TECHNIQUES.find((t) => t.key === form.technique)?.hint}</p>}
              </div>

              {/* PHOTO UPLOAD */}
              <div data-testid="photo-section">
                <span className="text-sm text-foreground/70 mb-2 block">Ajoutez une photo (optionnel)</span>
                <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" onChange={onFile} className="hidden" data-testid="perso-photo-input" />
                {!previewUrl ? (
                  <button type="button" onClick={() => fileRef.current?.click()} data-testid="perso-photo-btn" className="w-full border-2 border-dashed border-border rounded-2xl px-4 py-8 flex flex-col items-center gap-2 text-muted-foreground hover:border-primary hover:text-primary transition-colors">
                    <Upload className="w-6 h-6" />
                    <span className="text-sm">Cliquez pour téléverser une photo souvenir</span>
                    <span className="text-xs">JPEG, PNG ou WEBP — max 10 Mo</span>
                  </button>
                ) : (
                  <div className="flex items-center gap-4 bg-card border border-border rounded-2xl p-3">
                    <img src={previewUrl} alt="Aperçu de la photo" className="w-20 h-20 object-cover rounded-xl" />
                    <span className="text-sm text-foreground/70 flex-1 truncate">{file?.name}</span>
                    <button type="button" onClick={clearFile} className="p-2 rounded-full hover:bg-accent transition-colors" data-testid="perso-photo-remove"><X className="w-4 h-4" /></button>
                  </div>
                )}
              </div>

              <label className="block">
                <span className="text-sm text-foreground/70 mb-2 block">Produit à personnaliser (optionnel)</span>
                <select className={inputCls} data-testid="perso-product" value={form.product} onChange={(e) => setForm({ ...form, product: e.target.value })}>
                  <option value="">— Sélectionner un objet —</option>
                  {products.map((p) => <option key={p.slug} value={p.name}>{p.name}</option>)}
                </select>
              </label>

              <div data-testid="suggestions-section">
                <div className="flex items-center gap-2 mb-3">
                  <Sparkles className="w-4 h-4 text-primary" />
                  <span className="text-sm text-foreground/70">Suggestions — cliquez pour ajouter à votre message</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {SUGGESTIONS.map((s, i) => (
                    <button key={i} type="button" data-testid={`suggestion-${i}`} onClick={() => applySuggestion(s.text)} title={s.text}
                      className="rounded-full border border-border bg-card px-4 py-2 text-sm hover:border-primary hover:bg-accent transition-colors">
                      <span className="text-primary mr-1">+</span>{s.label}
                    </button>
                  ))}
                </div>
              </div>

              <label className="block">
                <span className="text-sm text-foreground/70 mb-2 block">Votre message libre</span>
                <div className="relative">
                  <PenLine className="w-4 h-4 text-muted-foreground absolute left-4 top-4" />
                  <textarea required rows={5} className={inputCls + " pl-11"} data-testid="perso-message" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="Ex : « Pour Mamie, avec tout notre amour — Été 1962, la maison bleue... »" />
                </div>
                <span className="text-xs text-muted-foreground mt-2 block">{form.message.length} caractères</span>
              </label>

              <button disabled={loading} data-testid="perso-submit-btn" className="rounded-full bg-primary text-primary-foreground px-9 py-4 flex items-center gap-2 hover:scale-[1.02] transition-transform duration-300 disabled:opacity-60">
                {loading && <Loader2 className="w-4 h-4 animate-spin" />} Valider ma personnalisation
              </button>
            </form>

            {/* PREVIEW */}
            <div className="lg:col-span-2">
              <div className="lg:sticky lg:top-28" data-testid="perso-preview">
                <p className="text-xs uppercase tracking-[0.25em] text-secondary-foreground/70 mb-3">Aperçu de votre personnalisation</p>
                <div className="bg-card rounded-3xl border border-border/50 overflow-hidden shadow-[0_20px_50px_rgb(0,0,0,0.05)]">
                  <div className="aspect-[4/3] bg-accent/30 flex items-center justify-center overflow-hidden">
                    {previewUrl ? (
                      <img src={previewUrl} alt="Aperçu personnalisation" className="w-full h-full object-cover" data-testid="preview-image" />
                    ) : (
                      <div className="text-center text-muted-foreground px-6">
                        <ImageIcon className="w-8 h-8 mx-auto mb-2" strokeWidth={1.4} />
                        <p className="text-sm">Votre photo apparaîtra ici</p>
                      </div>
                    )}
                  </div>
                  <div className="p-6">
                    {form.resident_first_name && <p className="font-serif-display text-2xl" data-testid="preview-resident">Pour {form.resident_first_name}</p>}
                    <div className="flex flex-wrap gap-2 mt-2">
                      {form.theme && <span className="inline-block text-xs rounded-full bg-secondary/25 text-secondary-foreground px-3 py-1" data-testid="preview-theme">{form.theme}</span>}
                      {form.technique && <span className="inline-block text-xs rounded-full bg-primary/15 text-primary px-3 py-1" data-testid="preview-technique">Technique : {form.technique}</span>}
                    </div>
                    <p className="mt-4 text-foreground/70 italic leading-relaxed" data-testid="preview-message">
                      {form.message ? `« ${form.message} »` : "Votre message doux s'affichera ici…"}
                    </p>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground mt-3 text-center">Aperçu simplifié — le rendu final sera confirmé avec vous.</p>
              </div>
            </div>
          </div>
        )}
      </section>
    </div>
  );
};

export default Personnalisation;
